import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Search, Loader2, UserCheck } from 'lucide-react';

/** Step 1 wizard: anagrafica cliente + referente + tecnico + sede legale. */
export default function WizardStepCliente({ cliente, setCliente, tecnici, lookupLoading, onLookupPiva, onPivaChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase">Ragione Sociale *</Label>
          <Input
            value={cliente.ragione_sociale}
            onChange={(e) => setCliente({...cliente, ragione_sociale: e.target.value})}
            placeholder="Nome azienda o ditta"
            className="rounded-none"
            data-testid="wizard-ragione-sociale"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Partita IVA</Label>
          <div className="flex gap-1">
            <Input
              value={cliente.partita_iva}
              onChange={(e) => onPivaChange(e.target.value)}
              className="rounded-none font-mono flex-1"
              placeholder="11 cifre"
              data-testid="wizard-partita-iva"
            />
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="rounded-none border-primary text-primary shrink-0"
              onClick={onLookupPiva}
              disabled={lookupLoading}
              title="Compila automaticamente i dati aziendali (VIES)"
              data-testid="wizard-piva-lookup-btn"
            >
              {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Compilazione automatica all&apos;11ª cifra (o clicca la lente)</p>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Codice Fiscale</Label>
          <Input
            value={cliente.codice_fiscale}
            onChange={(e) => setCliente({...cliente, codice_fiscale: e.target.value})}
            className="rounded-none font-mono"
            data-testid="wizard-cf"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">SDI (Codice Destinatario)</Label>
          <Input
            value={cliente.sdi}
            onChange={(e) => setCliente({...cliente, sdi: e.target.value})}
            className="rounded-none font-mono"
            maxLength={7}
            data-testid="wizard-sdi"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Telefono</Label>
          <Input
            value={cliente.telefono}
            onChange={(e) => setCliente({...cliente, telefono: e.target.value})}
            className="rounded-none"
            data-testid="wizard-telefono"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Email</Label>
          <Input
            type="email"
            value={cliente.email}
            onChange={(e) => setCliente({...cliente, email: e.target.value})}
            className="rounded-none"
            data-testid="wizard-email"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">PEC</Label>
          <Input
            type="email"
            value={cliente.pec}
            onChange={(e) => setCliente({...cliente, pec: e.target.value})}
            className="rounded-none"
            placeholder="es. azienda@pec.it"
            data-testid="wizard-pec"
          />
        </div>
      </div>

      {/* Referente */}
      <div className="border-t border-border pt-4 mt-4">
        <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Referente (opzionale)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Nome Referente</Label>
            <Input
              value={cliente.referente_nome}
              onChange={(e) => setCliente({...cliente, referente_nome: e.target.value})}
              placeholder="Nome e cognome"
              className="rounded-none"
              data-testid="wizard-referente-nome"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Email Referente</Label>
            <Input
              type="email"
              value={cliente.referente_email}
              onChange={(e) => setCliente({...cliente, referente_email: e.target.value})}
              className="rounded-none"
              data-testid="wizard-referente-email"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Cellulare Referente</Label>
            <Input
              value={cliente.referente_cellulare}
              onChange={(e) => setCliente({...cliente, referente_cellulare: e.target.value})}
              className="rounded-none"
              data-testid="wizard-referente-cellulare"
            />
          </div>
        </div>
      </div>

      {/* Tecnico Assegnato (obbligatorio) */}
      <div className="border-t border-border pt-4 mt-4">
        <p className="text-xs font-mono uppercase text-muted-foreground mb-3 flex items-center gap-2">
          <UserCheck className="h-3 w-3" /> Tecnico / Agente di riferimento *
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Tecnico Assegnato *</Label>
            {tecnici.length > 0 ? (
              <Select
                value={cliente.tecnico_assegnato_id || ''}
                onValueChange={(value) => {
                  if (value === '__manual__') {
                    setCliente({ ...cliente, tecnico_assegnato_id: '', tecnico_assegnato_nome: '' });
                    return;
                  }
                  const t = tecnici.find(x => x.id === value);
                  setCliente({
                    ...cliente,
                    tecnico_assegnato_id: value,
                    tecnico_assegnato_nome: t ? `${t.nome || ''} ${t.cognome || ''}`.trim() || t.email : ''
                  });
                }}
              >
                <SelectTrigger className="rounded-none" data-testid="wizard-tecnico-select">
                  <SelectValue placeholder="Seleziona un tecnico" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {tecnici.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {`${t.nome || ''} ${t.cognome || ''}`.trim() || t.email}
                    </SelectItem>
                  ))}
                  <SelectItem value="__manual__">Altro (inserisci nome manualmente)</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={cliente.tecnico_assegnato_nome}
                onChange={(e) => setCliente({ ...cliente, tecnico_assegnato_nome: e.target.value })}
                placeholder="Nome e cognome tecnico"
                className="rounded-none"
                data-testid="wizard-tecnico-nome"
              />
            )}
          </div>
          {(tecnici.length > 0 && !cliente.tecnico_assegnato_id) && (
            <div>
              <Label className="text-xs font-mono uppercase">Nome Tecnico (manuale) *</Label>
              <Input
                value={cliente.tecnico_assegnato_nome}
                onChange={(e) => setCliente({ ...cliente, tecnico_assegnato_nome: e.target.value })}
                placeholder="Nome e cognome"
                className="rounded-none"
                data-testid="wizard-tecnico-nome"
              />
            </div>
          )}
        </div>
      </div>

      {/* Sede Legale */}
      <div className="border-t border-border pt-4 mt-4">
        <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Sede Legale</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Indirizzo</Label>
            <Input
              value={cliente.indirizzo_legale}
              onChange={(e) => setCliente({...cliente, indirizzo_legale: e.target.value})}
              className="rounded-none"
              data-testid="wizard-indirizzo"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Città</Label>
            <Input
              value={cliente.citta_legale}
              onChange={(e) => setCliente({...cliente, citta_legale: e.target.value})}
              className="rounded-none"
              data-testid="wizard-citta"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-mono uppercase">CAP</Label>
              <Input
                value={cliente.cap_legale}
                onChange={(e) => setCliente({...cliente, cap_legale: e.target.value})}
                className="rounded-none"
                data-testid="wizard-cap"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Prov.</Label>
              <Input
                value={cliente.provincia_legale}
                onChange={(e) => setCliente({...cliente, provincia_legale: e.target.value})}
                className="rounded-none"
                maxLength={2}
                data-testid="wizard-provincia"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs font-mono uppercase">Note</Label>
        <Textarea
          value={cliente.note}
          onChange={(e) => setCliente({...cliente, note: e.target.value})}
          className="rounded-none"
          rows={2}
          data-testid="wizard-note"
        />
      </div>
    </div>
  );
}
